import React from "react";

interface Props {
  /** The entity's own colour — tinted for the fill and the border. */
  color: string;
  label: string;
  size?: "sm" | "md";
  /**
   * A solid dot before the label. Status must never be conveyed by colour alone — the dot plus the
   * label text both carry meaning independent of hue, which a colour-only pill did not.
   */
  dot?: boolean;
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<Props["size"]>, string> = {
  sm: "text-xs px-2 py-0.5 gap-1",
  md: "text-sm px-2.5 py-1 gap-1.5",
};

/**
 * The status pill used across the app — previously copy-pasted in `BadgeCell` and
 * `PreviousAppointmentsList`. One place for the `color + "20"` fill / `color + "40"` border
 * convention every status colour in `entities/appointment` is chosen to work with.
 */
export const Badge: React.FC<Props> = ({ color, label, size = "md", dot = false, className }) => (
  <span
    className={`inline-flex items-center rounded-full font-medium ${SIZE_CLASS[size]} ${className ?? ""}`}
    style={{
      color,
      backgroundColor: color + "20",
      border: `1px solid ${color}40`,
    }}
  >
    {dot && (
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
    )}
    {label}
  </span>
);
